import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API, errMsg } from "../lib/api";
import { toast } from "sonner";

export default function Onboarding() {
  const navigate = useNavigate();
  const [year, setYear] = useState("");
  const [exam, setExam] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [saving, setSaving] = useState(false);

  const finish = async (skip = false) => {
    setSaving(true);
    try {
      if (!skip) {
        await API.put("/account/onboarding", {
          year_of_study: year || null, target_exam: exam || null,
          preferred_difficulty: difficulty || null, weak_areas: [],
        });
      }
      navigate("/app/topics");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        <Logo />
        <h1 className="mt-10 font-serif text-3xl md:text-4xl font-bold tracking-tight" data-testid="onboarding-title">Welcome to PAMind</h1>
        <p className="mt-2 text-sm text-muted-foreground">A few optional questions to personalise your revision. Based on your choices, we'll suggest starting with 20 mixed-difficulty questions.</p>
        <div className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label>Current year of study</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="onboarding-year-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="year1" data-testid="year-option-year1">PA Year 1</SelectItem>
                <SelectItem value="year2" data-testid="year-option-year2">PA Year 2</SelectItem>
                <SelectItem value="qualified" data-testid="year-option-qualified">Newly qualified PA</SelectItem>
                <SelectItem value="other" data-testid="year-option-other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Target exam</Label>
            <Select value={exam} onValueChange={setExam}>
              <SelectTrigger data-testid="onboarding-exam-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="university" data-testid="exam-option-university">University SBA exams</SelectItem>
                <SelectItem value="para" data-testid="exam-option-para">PARA knowledge-based assessment</SelectItem>
                <SelectItem value="general" data-testid="exam-option-general">General clinical revision</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Preferred starting difficulty</Label>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger data-testid="onboarding-difficulty-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="easy" data-testid="difficulty-option-easy">Easy</SelectItem>
                <SelectItem value="mixed" data-testid="difficulty-option-mixed">Mixed</SelectItem>
                <SelectItem value="hard" data-testid="difficulty-option-hard">Challenge me</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button data-testid="onboarding-finish-btn" onClick={() => finish(false)} disabled={saving} className="px-8">
            Start my first session <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button variant="ghost" data-testid="onboarding-skip-btn" onClick={() => finish(true)} disabled={saving}>
            Skip for now
          </Button>
        </div>
      </div>
    </div>
  );
}
